import { createSelector } from "reselect";
import _ from "lodash";
import { TYPE_ACTIVITY, TYPE_TEMPLATE } from "../actions/types";
import { TYPE_PROPOSAL } from "../config/constants";
import { formatDateIntl } from "../utils/DateUtils";

export const getLiveReducer = state => state.liveReducer;
export const getEntitiesReducer = state => state.entitiesReducer;
export const getGlobalInformationsReducer = state =>
  state.globalInformationsReducer;
export const getProfileReducer = state => state.profileReducer;
export const getTemplateReducer = state => state.templateReducer;
export const getErrorReducer = state => state.errorReducer;
export const getLoginReducer = state => state.loginReducer;
export const getCommunityReducer = state => state.communityReducer;
export const getUrlscanReducer = state => state.urlscanReducer;
export const getChatReducer = state => state.chatReducer;
export const getEventInterestedUsersReducer = state =>
  state.eventInterestedUsersReducer;
export const getAdReducer = state => state.adReducer;
export const getDeeplinkReducer = state => state.deeplinkReducer;

const getCategories = state => state.entitiesReducer.categories;
const getTemplates = state => state.entitiesReducer.templates;
const getUserId = state =>
  state.profileReducer.user ? state.profileReducer.user._id : null;

export const getActivities = createSelector([getTemplates], templates =>
  _.pickBy(templates, template => template.type === TYPE_ACTIVITY)
);

export const getProposals = createSelector([getTemplates], templates =>
  _.pickBy(templates, template => template.type === TYPE_PROPOSAL)
);

export const getTypeTemplates = createSelector([getTemplates], templates =>
  _.pickBy(templates, template => template.type === TYPE_TEMPLATE)
);

export const getUserTemplates = createSelector(
  [getTemplates, getUserId],
  (templates, userId) =>
    _.pickBy(
      templates,
      template => template.type === TYPE_TEMPLATE && template.userId === userId
    )
);

export const getNotifTemplates = createSelector([getTemplates], templates =>
  _.pickBy(templates, template => template.acceptNotif)
);

export const getCategoriesForActivities = createSelector(
  [getCategories, getActivities],
  getFilteredCategories()
);

export const getCategoriesForProposals = createSelector(
  [getCategories, getProposals],
  getFilteredCategories()
);

export const getCategoriesForTemplates = createSelector(
  [getCategories, getTypeTemplates],
  getFilteredCategories()
);

export const getCategoriesForUser = createSelector(
  [getCategories, getUserTemplates],
  getFilteredCategories()
);

export const getCategoriesForNotif = createSelector(
  [getCategories, getNotifTemplates],
  getFilteredCategories()
);

export const getLoadingAppSelector = createSelector(
  [
    getProfileReducer,
    getGlobalInformationsReducer,
    getErrorReducer,
    getLoginReducer,
    getCommunityReducer
  ],
  (
    { user },
    { initialUrl, clientVersion, serverVersion, accessDenied },
    { dropDownAlertError },
    { fromLogin },
    { currentCommunity, isNotFound }
  ) => {
    return {
      user,
      initialUrl,
      clientVersion,
      serverVersion,
      accessDenied,
      dropDownAlertError,
      fromLogin,
      currentCommunity,
      isNotFound
    };
  }
);

export const getLandingSelector = createSelector(
  [
    getGlobalInformationsReducer,
    getErrorReducer,
    getUrlscanReducer,
    getCommunityReducer,
    getDeeplinkReducer,
    getLoginReducer
  ],
  (
    { accessDenied, dropDownAlertInfoGI, dropDownAlertErrorGI, initialLink },
    { dropDownAlertError, dropDownAlertInfo },
    { link },
    {
      cities,
      currentCommunity,
      computedCommunities,
      needRegisterKey,
      currentCity
    },
    { deeplink },
    { showLoginModal, routeName }
  ) => {
    return {
      accessDenied,
      dropDownAlertInfoGI,
      dropDownAlertErrorGI,
      initialLink,
      dropDownAlertError,
      dropDownAlertInfo,
      link,
      cities,
      currentCommunity,
      computedCommunities,
      needRegisterKey,
      currentCity,
      deeplink,
      showLoginModal,
      isLandingScreen: Boolean(routeName === "LandingScreen")
    };
  }
);

export const getLoginSelector = createSelector(
  [
    getLoginReducer,
    getGlobalInformationsReducer,
    getErrorReducer,
    getCommunityReducer,
    getProfileReducer
  ],
  (
    {
      isFetching,
      emailOrPseudo,
      emailOrPseudoErrorMsg,
      password,
      passwordErrorMsg,
      valid,
      clearValidate,
      registerkey,
      registerkeyErrorMsg,
      clearValidateApple,
      validApple,
      clearForgetPwdValidate,
      validForgetPwd,
      showLoginModal,
      selectedCategory,
      token,
      tokenErrorMsg,
      hideRegisterKey,
      isTooltip3Visible,
      isTooltip4Visible,
      isTooltip6Visible,
      clearValidateSignupGuest,
      validSignupGuest,
      fromLogin
    },
    { lang, installationId },
    { dropDownAlertInfo, dropDownAlertError },
    { currentCommunity },
    { user, strategy }
  ) => {
    return {
      isFetching,
      emailOrPseudo,
      emailOrPseudoErrorMsg,
      password,
      passwordErrorMsg,
      valid,
      clearValidate,
      registerkey,
      registerkeyErrorMsg,
      clearValidateApple,
      validApple,
      clearForgetPwdValidate,
      validForgetPwd,
      showLoginModal,
      selectedCategory,
      token,
      tokenErrorMsg,
      hideRegisterKey,
      isTooltip3Visible,
      isTooltip4Visible,
      isTooltip6Visible,
      clearValidateSignupGuest,
      validSignupGuest,
      fromLogin,
      lang,
      installationId,
      dropDownAlertInfo,
      dropDownAlertError,
      currentCommunity,
      user,
      strategy
    };
  }
);

export const getAppNavigationSelector = createSelector(
  [
    getProfileReducer,
    getGlobalInformationsReducer,
    getTemplateReducer,
    getCommunityReducer,
    getLoginReducer
  ],
  (
    { user },
    { accessDenied = false, lang },
    { displayPreview },
    { currentCommunity },
    { isFetching }
  ) => {
    return {
      user,
      accessDenied,
      lang,
      displayPreview,
      communityDetails: currentCommunity,
      isFetching
    };
  }
);

export const getProfileUpdateSelector = createSelector(
  [
    getProfileReducer,
    getGlobalInformationsReducer,
    getEntitiesReducer,
    getLoginReducer
  ],
  (
    {
      files,
      pseudo,
      firstname,
      lastname,
      gender,
      birthdate,
      address,
      zipCode,
      city,
      countryCode,
      phone,
      description,
      photoUri,
      hobbiesIds,
      startDate,
      endDate,
      locationId,
      pseudoErrorMsg,
      firstnameErrorMsg,
      lastnameErrorMsg,
      birthdayErrorMsg,
      daterangeErrorMsg,
      locationErrorMsg,
      user,
      isValid,
      isFetching,
      dateRangeChanged,
      locationIdChanged,
      firstLogin,
      needPseudoCheck,
      clearProfileValidate,
      isPseudoClearValidate,
      phoneErrorMsg,
      needRegisterForPush,
      updateHobbies,
      strategy,
      newpassword,
      newpasswordconfirmation,
      passwordErrorMsg,
      passwordConfirmationErrorMsg
    },
    { lang },
    { locations },
    { deleteUserResult }
  ) => {
    return {
      files,
      pseudo,
      firstname,
      lastname,
      gender,
      birthdate,
      address,
      zipCode,
      city,
      countryCode,
      phone,
      description,
      photoUri,
      hobbiesIds,
      startDate,
      endDate,
      locationId,
      pseudoErrorMsg,
      firstnameErrorMsg,
      lastnameErrorMsg,
      birthdayErrorMsg,
      daterangeErrorMsg,
      locationErrorMsg,
      user,
      isValid,
      isFetching,
      dateRangeChanged,
      locationIdChanged,
      firstLogin,
      needPseudoCheck,
      clearProfileValidate,
      isPseudoClearValidate,
      phoneErrorMsg,
      needRegisterForPush,
      updateHobbies,
      strategy,
      newpassword,
      newpasswordconfirmation,
      passwordErrorMsg,
      passwordConfirmationErrorMsg,
      locale: lang,
      locations,
      deleteUserResult
    };
  }
);

export const getProfileSelector = createSelector(
  [getProfileReducer, getEntitiesReducer],
  (
    {
      isFetching,
      photoUri,
      hobbiesIds,
      pseudo,
      firstname,
      lastname,
      age,
      gender,
      city,
      zipCode,
      description,
      countryCode,
      startDate,
      endDate,
      _id,
      role,
      communityId,
      user,
      phone,
      company
    },
    { communities }
  ) => {
    return {
      isFetching,
      photoUri,
      hobbiesIds,
      pseudo,
      firstname,
      lastname,
      age,
      description,
      gender,
      city,
      zipCode,
      countryCode,
      startDate,
      endDate,
      to: _id,
      role,
      myRole: user ? user.role : null,
      community: communityId && communities ? communities[communityId] : null,
      blockedUsersIds: user ? new Set(user.blockedUsersIds) : new Set(),
      phone,
      company
    };
  }
);

export const getChatSelector = createSelector(
  [
    getProfileReducer,
    getGlobalInformationsReducer,
    getEntitiesReducer,
    getChatReducer
  ],
  (
    { user },
    { lang },
    { templates },
    {
      messages,
      isFetching,
      hasNextPage,
      nextPage,
      page,
      image,
      files,
      location,
      readyToCopyUrl,
      title,
      to: chatTo,
      templateId,
      youtubeVideoId,
      reload
    }
  ) => {
    return {
      user,
      lang,
      templates,
      messages,
      isFetching,
      hasNextPage,
      nextPage,
      page,
      image,
      files,
      location,
      readyToCopyUrl,
      title,
      chatTo,
      templateId,
      youtubeVideoId,
      reload
    };
  }
);

export const getLiveSelector = createSelector(
  [
    getLiveReducer,
    getGlobalInformationsReducer,
    getProfileReducer,
    getDeeplinkReducer
  ],
  (
    {
      events,
      isFetching,
      liveOnTop,
      reload,
      nextPage,
      hasNextPage,
      filter,
      inLiveScreen
    },
    { initialLink },
    { user },
    { deeplink }
  ) => {
    return {
      events,
      isFetching,
      liveOnTop,
      reload,
      nextPage,
      hasNextPage,
      filter,
      inLiveScreen,
      initialLink,
      user,
      deeplink
    };
  }
);

export const getLiveEventSelector = createSelector(
  [
    getLiveReducer,
    getEntitiesReducer,
    getTemplateReducer,
    getProfileReducer,
    getAdReducer
  ],
  (
    { interestedusers, inLiveScreen },
    { templates, locations },
    { equipments, templateDates, templateTimes },
    { user },
    { isFetching, ad }
  ) => {
    return {
      interestedusers,
      inLiveScreen,
      templates,
      locations,
      equipments,
      templateDates,
      templateTimes,
      user,
      inLiveScreen,
      isFetching,
      ad
    };
  }
);

export const getLiveHeaderSelector = date =>
  createSelector(
    [getProfileReducer, getLiveReducer, getGlobalInformationsReducer],
    ({ user }, { users }, { lang, datetimeDiff }) => {
      const dateStr = formatDateIntl(lang, date, datetimeDiff);
      return {
        user,
        users,
        dateStr
      };
    }
  );

export const getErrorManagementSelector = createSelector(
  [getErrorReducer, getLoginReducer, getGlobalInformationsReducer],
  (
    {
      isMaintenance,
      dropDownAlertInfo,
      dropDownAlertError,
      dropDownAlertMessage,
      dropDownAutoClearAlertError,
      dropDownAutoClearAlertWarn,
      dropDownAutoClearAlertInfo,
      splashScreenHide,
      logout: errorlogout
    },
    { logout, logoutResult },
    {
      dropDownAlertInfo: dropDownAlertInfoGI,
      dropDownAlertWarn,
      dropDownAlertError: dropDownAlertErrorGI
    }
  ) => {
    return {
      isMaintenance,
      dropDownAlertInfo: dropDownAlertInfo || dropDownAlertInfoGI,
      dropDownAlertError: dropDownAlertError || dropDownAlertErrorGI,
      dropDownAlertMessage,
      dropDownAutoClearAlertError,
      dropDownAutoClearAlertWarn,
      dropDownAutoClearAlertInfo,
      splashScreenHide,
      logout: logout || errorlogout,
      logoutResult,
      dropDownAlertWarn
    };
  }
);

function getFilteredCategories() {
  return (categories, templates) =>
    _.pickBy(
      categories,
      category =>
        _.filter(category.templateIds, templateId => templates[templateId])
          .length > 0
    );
}
